import React, { useEffect, useState } from 'react';

import { noOp } from '@neslotech/utils';

import { useAppDispatch, useAppSelector } from '../hooks';

import { ClientActions } from '../actions/client/client.actions';
import { CategoryActions } from '../actions/category/category.actions';

import type { Client } from '../interfaces/client/client.interface';
import type { Category } from '../interfaces/config/category.interface';
import type { Profile } from '../interfaces/profile/profile.interface';

import Modal from './components/Modal';

// =============================================================================
//  Create client modal (v3)
//
//  Backend-required fields:
//    name (unique), category_id, user_id (sourced here from profile_store so
//    the new client is owned by the current user — mirrors the legacy
//    AddClient.container.tsx behaviour).  client_type_id has a DB default of
//    1 ("basic"), so it does not need to be passed for v3 creates.
//
//  v3-relevant optional fields:
//    tier, description, geographic_focus, location.
//
//  Heavier setup (image upload, personnel roster, sectors join) still goes
//  through the legacy form at /core/clients/add.
// =============================================================================

const TIER_OPTIONS = [
  { value: 'strategic', label: 'Strategic' },
  { value: 'growth', label: 'Growth' },
  { value: 'emerging', label: 'Emerging' }
] as const;

interface Props {
  onClose: () => void;
}

const CreateClientModal = ({ onClose }: Props) => {
  const dispatch = useAppDispatch();
  const categories: Category[] =
    useAppSelector(({ category_store }) => category_store.categories) || [];
  // The backend requires user_id (belongs_to :user, not optional). The legacy
  // AddClient.container.tsx sources this from profile_store; we do the same so
  // the new client is owned by the current user.
  const profile: Profile | undefined = useAppSelector(({ profile_store }) => profile_store.profile);

  useEffect(() => {
    if (categories.length === 0) dispatch(CategoryActions.loadCategories(noOp));
  }, [dispatch, categories.length]);

  const [name, setName] = useState<string>('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [tier, setTier] = useState<string>('growth');
  const [description, setDescription] = useState<string>('');
  const [geographic_focus, setGeographicFocus] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const requiredOk = name.trim() !== '' && categoryId !== '' && !!profile?.id;

  const handleSave = () => {
    if (!requiredOk) return;
    setSaving(true);
    dispatch(
      ClientActions.addClient(
        {
          name: name.trim(),
          category_id: Number(categoryId),
          tier: tier as Client['tier'],
          description,
          geographic_focus,
          location,
          // Required by the backend's `belongs_to :user` (see Client model).
          user_id: profile?.id
        } as Client,
        // The legacy saga signature wants `navigate`.  We pass a no-op so the
        // legacy redirect doesn't fire — v3 stays on the list and the new
        // entry shows up via the saga's SET_CLIENT → reducer patch.
        ((): void => undefined) as never,
        () => {
          setSaving(false);
          onClose();
        }
      )
    );
  };

  const footer = (
    <>
      <button type="button" className="btn ghost" onClick={onClose} disabled={saving}>
        Cancel
      </button>
      <button
        type="button"
        className="btn primary"
        onClick={handleSave}
        disabled={saving || !requiredOk}
      >
        {saving ? 'Creating…' : 'Create client'}
      </button>
    </>
  );

  return (
    <Modal title="New client" onClose={onClose} footer={footer}>
      <div className="field">
        <span className="label">Client name *</span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Cape Industrial Holdings"
          autoFocus
        />
      </div>

      <div className="field">
        <span className="label">Category *</span>
        <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
          <option value="">— Select a category —</option>
          {categories.map((c) => (
            <option key={c.id} value={String(c.id)}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <span className="label">Tier</span>
        <select value={tier} onChange={(e) => setTier(e.target.value)}>
          {TIER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <span className="label">Description</span>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Short relationship summary"
        />
      </div>

      <div className="field row-2">
        <div className="field" style={{ gap: 6 }}>
          <span className="label">Geographic focus</span>
          <input
            type="text"
            value={geographic_focus}
            onChange={(e) => setGeographicFocus(e.target.value)}
            placeholder="e.g. EMEA"
          />
        </div>
        <div className="field" style={{ gap: 6 }}>
          <span className="label">Location</span>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Head office city"
          />
        </div>
      </div>

      <p className="muted" style={{ fontSize: 12, lineHeight: 1.4, marginTop: 4, marginBottom: 0 }}>
        Sector, personnel roster, client logo and excluded industries can be attached on the client
        detail page or via the legacy form at
        <code> /core/clients/add</code>.
      </p>
    </Modal>
  );
};

export default CreateClientModal;
